import { supabase, isSupabaseConfigured } from './supabaseClient';

export interface Vendor {
  id: string;
  name: string;
  websiteUrl: string;
  description?: string;
  logoUrl?: string;
  tags?: string[];
  couponCode?: string;
  couponDescription?: string;
  scrapedCouponCode?: string;
  scrapedCouponDiscount?: string;
  scrapedCouponExpiresAt?: string | null;
  scrapedCouponLastSeen?: string | null;
  scrapedCouponSource?: string | null;
}

class VendorService {
  private vendorsCache: Vendor[] | null = null;
  private lastFetchTime: number = 0;
  private fetchPromise: Promise<Vendor[]> | null = null;
  private readonly CACHE_DURATION = 30 * 60 * 1000; // 30 minutes

  /**
   * Fetch vendors from Supabase
   */
  async fetchVendors(): Promise<Vendor[]> {
    // If there's already a fetch in progress, return that promise
    if (this.fetchPromise) {
      return this.fetchPromise;
    }

    this.fetchPromise = (async () => {
      try {
        if (!isSupabaseConfigured()) {
          console.warn('Supabase is not configured, returning empty vendor list');
          return this.vendorsCache || [];
        }

        const { data, error } = await supabase
          .from('vendors')
          .select('*')
          .eq('enabled', true)
          .order('display_order', { ascending: true })
          .order('name', { ascending: true });

        if (error) {
          throw error;
        }

        // Transform Supabase data to Vendor format
        const vendors: Vendor[] = (data || []).map((row) => ({
          id: row.id,
          name: row.name,
          websiteUrl: row.website_url,
          description: row.description || undefined,
          logoUrl: row.logo_url || undefined,
          tags: row.tags || undefined,
          couponCode: row.coupon_code || undefined,
          couponDescription: row.coupon_description || undefined,
          scrapedCouponCode: row.scraped_coupon_code || undefined,
          scrapedCouponDiscount: row.scraped_coupon_discount || undefined,
          scrapedCouponExpiresAt: row.scraped_coupon_expires_at || undefined,
          scrapedCouponLastSeen: row.scraped_coupon_last_seen || undefined,
          scrapedCouponSource: row.scraped_coupon_source || undefined,
        }));

        this.vendorsCache = vendors;
        this.lastFetchTime = Date.now();

        return vendors;
      } catch (error) {
        console.error('Failed to fetch vendors from Supabase:', error);
        
        // Return cached vendors if available, otherwise empty array
        return this.vendorsCache || [];
      } finally {
        this.fetchPromise = null;
      }
    })();

    return this.fetchPromise;
  }

  /**
   * Get vendors (cached or fetch if needed)
   */
  async getVendors(forceRefresh: boolean = false): Promise<Vendor[]> {
    const now = Date.now();
    const cacheAge = now - this.lastFetchTime;
    const shouldRefresh = forceRefresh || 
      (cacheAge > this.CACHE_DURATION) ||
      this.lastFetchTime === 0;

    if (shouldRefresh) {
      // Fetch in background, don't block
      this.fetchVendors().catch((error) => {
        console.error('Background vendor refresh failed:', error);
      });
    }

    // Return cached vendors immediately if available
    if (this.vendorsCache) {
      return this.vendorsCache;
    }

    // If no cache, wait for fetch
    return this.fetchVendors();
  }

  /**
   * Get vendors synchronously (cached only)
   */
  getVendorsSync(): Vendor[] {
    return this.vendorsCache || [];
  }

  /**
   * Initialize vendors (call on app start)
   */
  async initialize(): Promise<void> {
    try {
      await this.fetchVendors();
    } catch (error) {
      console.error('Failed to initialize vendors:', error);
      // Don't throw - use cached/empty array
    }
  }
}

// Export singleton instance
export const vendorService = new VendorService();

